// Synthesized sound feedback via Web Audio API.
//
// We don't ship any audio files — every cue is generated inline from
// oscillators so the bundle stays tiny. Sounds are off by default
// (`localStorage.wipe_sounds === 'on'` to enable) so they never
// surprise a quiet office.
//
// All play functions are safe to call from any context — they no-op
// when sounds are disabled or when the AudioContext can't be created
// (which happens until the user interacts with the page on most
// browsers' autoplay policies).

const STORAGE_KEY = 'wipe_sounds';
let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (sharedCtx) return sharedCtx;
  try {
    const Ctor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
    if (!Ctor) return null;
    sharedCtx = new Ctor();
    return sharedCtx;
  } catch {
    return null;
  }
}

function isEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function setSoundsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch { /* ignore */ }
}

export function getSoundsEnabled(): boolean {
  return isEnabled();
}

interface ToneOptions {
  freq: number;        // Hz
  durationMs: number;  // total length
  type?: OscillatorType;
  volume?: number;     // 0..1
  attackMs?: number;   // attack ramp
  releaseMs?: number;  // release ramp
}

function tone({ freq, durationMs, type = 'sine', volume = 0.18, attackMs = 4, releaseMs = 60 }: ToneOptions, startOffset = 0): void {
  const ctx = getCtx();
  if (!ctx) return;
  // Resume context if it's been suspended (autoplay policy).
  if (ctx.state === 'suspended') void ctx.resume();
  const now = ctx.currentTime + startOffset / 1000;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);

  // ASR envelope to avoid clicks.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume, now + attackMs / 1000);
  gain.gain.linearRampToValueAtTime(volume, now + (durationMs - releaseMs) / 1000);
  gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.05);
}

function chord(freqs: number[], durationMs: number, type: OscillatorType = 'sine', volume = 0.12) {
  freqs.forEach((f) => tone({ freq: f, durationMs, type, volume }));
}

function arpeggio(freqs: number[], stepMs: number, type: OscillatorType = 'sine', volume = 0.16) {
  freqs.forEach((f, i) => tone({ freq: f, durationMs: stepMs + 40, type, volume }, i * stepMs));
}

// ── Public play functions ────────────────────────────────────────────

/** Vote up — ascending C-major arpeggio. */
export function playUp(): void {
  if (!isEnabled()) return;
  arpeggio([523.25, 659.25, 783.99], 70, 'triangle');
}

/** Vote down — descending minor arpeggio. */
export function playDown(): void {
  if (!isEnabled()) return;
  arpeggio([523.25, 415.30, 311.13], 70, 'triangle');
}

/** Post / follow sent — clean ding. */
export function playPostSent(): void {
  if (!isEnabled()) return;
  tone({ freq: 880, durationMs: 220, type: 'sine', volume: 0.18, attackMs: 6, releaseMs: 120 });
  tone({ freq: 1318.51, durationMs: 240, type: 'sine', volume: 0.12, attackMs: 6, releaseMs: 140 }, 60);
}

/** Spark fire — fast bright zap. */
export function playSparkFire(): void {
  if (!isEnabled()) return;
  tone({ freq: 1760, durationMs: 70, type: 'square', volume: 0.13, attackMs: 2, releaseMs: 30 });
  tone({ freq: 1318, durationMs: 80, type: 'square', volume: 0.1, attackMs: 2, releaseMs: 30 }, 50);
}

/** Streak / immortality / referral — triumphant chord. */
export function playMilestone(): void {
  if (!isEnabled()) return;
  chord([523.25, 659.25, 783.99, 1046.5], 460, 'sine', 0.13);
}

/** Time gift sent — soft dual tone. */
export function playGift(): void {
  if (!isEnabled()) return;
  tone({ freq: 698.46, durationMs: 200, type: 'sine', volume: 0.15 });
  tone({ freq: 1046.5, durationMs: 220, type: 'sine', volume: 0.1 }, 100);
}

/** Error — short low buzz. */
export function playError(): void {
  if (!isEnabled()) return;
  tone({ freq: 196, durationMs: 160, type: 'square', volume: 0.1, releaseMs: 80 });
}
